"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameConnection = void 0;
const models_1 = require("@creature-chess/models");
const wireProtocol_1 = require("~/plugins/wireProtocol");
const sync_1 = require("~/store/board/sync");
const network_1 = require("~/store/game/network");
const actions_1 = require("~/store/game/ui/actions");
const sync_2 = require("~/store/settings/sync");
const creatureDefinitions_1 = require("./creatureDefinitions");
const types_1 = require("./types");
class GameConnection {
    constructor(socket, dispatch) {
        this.socket = socket;
        this.dispatch = dispatch;
        this.cleanupFns = [];
        this.pingInterval = null;
        this.setupListeners();
        this.startPingLoop();
    }
    handleConnected(payload) {
        const { players, game: { phase, phaseStartedAtSeconds, round }, settings, creatures, } = payload;
        (0, creatureDefinitions_1.setCreatureDefinitions)(creatures);
        // setSettingsAction must be first, as the listener creates the GameSession.
        this.dispatch((0, sync_2.setSettingsAction)(settings));
        this.dispatch(models_1.GameEvents.playerListChangedEvent({ players }));
        this.dispatch(models_1.GameEvents.gamePhaseStartedEvent({
            phase,
            startedAt: phaseStartedAtSeconds,
            round,
        }));
        this.dispatch((0, actions_1.setInGameCommand)());
        this.dispatch((0, actions_1.setConnectionStatusCommand)(types_1.ConnectionStatus.CONNECTED));
    }
    sendPlayerAction(action) {
        if (!wireProtocol_1.wireProtocol.acceptsOutbound(action.type)) {
            console.error(`Invalid player action type: ${action.type}`);
            return;
        }
        const sent = Date.now();
        this.socket.emit("sendPlayerActions", action, () => {
            const rtt = Date.now() - sent;
            this.dispatch((0, network_1.setPing)(rtt));
        });
    }
    sendFinishMatch() {
        this.socket.emit("finishMatch", { empty: true });
    }
    destroy() {
        this.stopPingLoop();
        for (const cleanup of this.cleanupFns) {
            cleanup();
        }
        this.cleanupFns = [];
    }
    setupListeners() {
        this.setupSocketListener("boardUpdate", (packet) => {
            this.dispatch((0, sync_1.boardUpdateAction)(packet));
        });
        this.setupSocketListener("benchUpdate", (packet) => {
            this.dispatch((0, sync_1.benchUpdateAction)(packet));
        });
        this.setupSocketListener("matchBoardUpdate", (packet) => {
            this.dispatch((0, sync_1.matchBoardUpdateAction)(packet));
        });
        this.setupActionListener("sendGameEvents", "gameEvents");
        this.setupActionListener("sendLocalPlayerEvents", "playerEvents");
        this.setupActionListener("playerInfoUpdates", "playerInfoUpdates");
        this.setupSocketListener("snapshot", (actions) => {
            for (const action of actions) {
                if (!wireProtocol_1.wireProtocol.acceptsInbound("playerInfoUpdates", action.type)) {
                    console.error(`Unhandled snapshot action type: ${action.type}`);
                    continue;
                }
                this.dispatch(action);
            }
        });
        // reconnect_failed/reconnect_error are Manager-level events in socket.io v4
        const onDisconnected = () => {
            this.dispatch((0, actions_1.setConnectionStatusCommand)(types_1.ConnectionStatus.DISCONNECTED));
        };
        this.socket.io.on("reconnect_failed", onDisconnected);
        this.socket.io.on("reconnect_error", onDisconnected);
        this.cleanupFns.push(() => this.socket.io.off("reconnect_failed", onDisconnected), () => this.socket.io.off("reconnect_error", onDisconnected));
    }
    setupSocketListener(event, handler) {
        this.socket.on(event, handler);
        this.cleanupFns.push(() => this.socket.off(event, handler));
    }
    setupActionListener(socketEvent, channel) {
        this.setupSocketListener(socketEvent, (action, ack) => {
            if (ack) {
                ack();
            }
            const validation = wireProtocol_1.wireProtocol.validateInbound(channel, action.type, action.payload);
            if (!validation.ok) {
                console.error(`Rejected ${socketEvent} of type "${action.type}": ${validation.reason}`);
                return;
            }
            this.dispatch(Object.assign(Object.assign({}, action), { payload: validation.payload }));
        });
    }
    startPingLoop() {
        this.pingInterval = setInterval(() => {
            const sent = Date.now();
            this.socket.emit("ping", { type: "ping" }, () => {
                const rtt = Date.now() - sent;
                this.dispatch((0, network_1.setPing)(rtt));
            });
        }, 2500);
    }
    stopPingLoop() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }
}
exports.GameConnection = GameConnection;
